// ViewToolbarFieldChooser.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ViewToolbarFieldChooser.css';

// Icons
import Icon from 'svg/Icon';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';
import PopupSeparator from 'components/popup/separator/PopupSeparator';

const normalizeValue = (value) => String(value || '').trim();

/**
 * ViewToolbarFieldChooser
 *
 * Organic view-options control: field checklist or Calendar date-field radios.
 * Unset properties follow schema "Display in default lists" (usually none).
 *
 * @param {Array<{ key: string, label: string, displayInDefaultLists?: boolean }>} fieldOptions
 * @param {string[]|null} properties - null means schema default-list fields
 * @param {function} onPropertiesChange
 * @param {boolean} [isCalendarMode=false]
 * @param {Array<{ key: string, label: string }>} [calendarFields]
 * @param {string|null} [calendarFieldKey]
 * @param {function} [onCalendarFieldChange]
 * @returns {ReactNode|null}
 */
const ViewToolbarFieldChooser = React.memo(({
    fieldOptions = [],
    properties = null,
    onPropertiesChange,
    isCalendarMode = false,
    calendarFields = [],
    calendarFieldKey = null,
    onCalendarFieldChange,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- REFS ----- //

    const anchorRef = useRef(null);

    // ----- STATE ----- //

    const [popupOpen, setPopupOpen] = useState(false);

    // ----- MEMOS ----- //

    const fields = useMemo(() => (
        Array.isArray(fieldOptions) ? fieldOptions.filter((field) => field?.key) : []
    ), [fieldOptions]);

    const dateFields = useMemo(() => (
        Array.isArray(calendarFields) ? calendarFields.filter((field) => field?.key) : []
    ), [calendarFields]);

    const defaultSelectedSet = useMemo(() => (
        new Set(
            fields
                .filter((field) => field?.displayInDefaultLists === true)
                .map((field) => normalizeValue(field.key))
                .filter(Boolean),
        )
    ), [fields]);

    const selectedSet = useMemo(() => {
        if (!Array.isArray(properties)) return null;
        return new Set(properties.map((key) => normalizeValue(key)).filter(Boolean));
    }, [properties]);

    const effectiveSelectedSet = selectedSet || defaultSelectedSet;
    const allSelected = fields.length > 0 && effectiveSelectedSet.size === fields.length;
    const matchesSchemaDefaults = effectiveSelectedSet.size === defaultSelectedSet.size
        && [...effectiveSelectedSet].every((key) => defaultSelectedSet.has(key));

    const isFieldSelected = (fieldKey) => effectiveSelectedSet.has(fieldKey);

    const triggerTitle = isCalendarMode
        ? (dateFields.find((field) => field.key === calendarFieldKey)?.label || 'Date field')
        : (allSelected ? 'All fields' : `${effectiveSelectedSet.size} fields`);

    const isActive = isCalendarMode
        ? Boolean(calendarFieldKey)
        : !matchesSchemaDefaults;

    // ----- HANDLERS ----- //

    /**
     * handleToggleField
     *
     * Toggles one checklist field and materializes an explicit selection when needed.
     *
     * @param {string} fieldKey
     */
    const handleToggleField = (fieldKey) => {
        const key = normalizeValue(fieldKey);
        if (!key || !fields.length) return;

        const currentlySelected = fields
            .map((field) => field.key)
            .filter((candidate) => effectiveSelectedSet.has(candidate));

        const nextSelected = currentlySelected.includes(key)
            ? currentlySelected.filter((candidate) => candidate !== key)
            : [...currentlySelected, key];

        onPropertiesChange?.(nextSelected);
    };

    /**
     * handleSelectAll
     *
     * Shows every field. Persists an explicit full list so it stays distinct
     * from the schema default (usually none).
     */
    const handleSelectAll = () => {
        onPropertiesChange?.(fields.map((field) => field.key));
    };

    /**
     * handleClear
     *
     * Leaves title-only (empty explicit selection).
     */
    const handleClear = () => {
        onPropertiesChange?.([]);
    };

    /**
     * handleCalendarSelect
     *
     * @param {string} fieldKey
     */
    const handleCalendarSelect = (fieldKey) => {
        onCalendarFieldChange?.(fieldKey);
        setPopupOpen(false);
    };

    if (isCalendarMode) {
        if (!dateFields.length) return null;
    } else if (!fields.length) {
        return null;
    }

    // ----- RENDER ----- //

    return (
        <>
            <div className="view-toolbar-field-chooser-container">

                {/* TRIGGER */}
                <div
                    className="view-toolbar-field-chooser-trigger"
                    style={{
                        color: isActive ? theme.foreground : theme.foregroundMuted,
                    }}
                    ref={anchorRef}
                    title={triggerTitle}
                    onClick={() => setPopupOpen((prev) => !prev)}
                >
                    <Icon
                        iconKey="view_column"
                        width={20}
                        height={20}
                        color={isActive ? theme.foreground : theme.foregroundMuted}
                    />
                    <Icon
                        iconKey="keyboard_arrow_down"
                        width={18}
                        height={18}
                        color={theme.foregroundMuted}
                    />
                </div>
            </div>

            <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>

                {/* POPUP */}
                <div
                    className="view-toolbar-field-chooser-popup"
                    style={{
                        maxHeight: '560px',
                        maxWidth: '280px',
                    }}
                >
                    {isCalendarMode ? (
                        dateFields.map((field) => {
                            const fieldKey = normalizeValue(field.key);
                            const isSelected = fieldKey === calendarFieldKey;

                            return (
                                <PopupOption
                                    key={`calendar:${fieldKey}`}
                                    label={String(field.label || fieldKey).trim() || fieldKey}
                                    rightContent={isSelected ? (
                                        <Icon
                                            iconKey="check"
                                            width={16}
                                            height={16}
                                            color={theme.foregroundMuted}
                                        />
                                    ) : null}
                                    onClick={() => handleCalendarSelect(fieldKey)}
                                />
                            );
                        })
                    ) : (
                        <>
                            {fields.map((field) => {
                                const fieldKey = normalizeValue(field.key);
                                const isSelected = isFieldSelected(fieldKey);

                                return (
                                    <PopupOption
                                        key={`field:${fieldKey}`}
                                        label={String(field.label || fieldKey).trim() || fieldKey}
                                        rightContent={isSelected ? (
                                            <Icon
                                                iconKey="check"
                                                width={16}
                                                height={16}
                                                color={theme.foregroundMuted}
                                            />
                                        ) : null}
                                        onClick={() => handleToggleField(fieldKey)}
                                    />
                                );
                            })}

                            <PopupSeparator />

                            <PopupOption
                                label="Select all"
                                labelColor={theme.foregroundMuted}
                                onClick={handleSelectAll}
                            />
                            <PopupOption
                                label="Clear"
                                labelColor={theme.foregroundMuted}
                                onClick={handleClear}
                            />
                        </>
                    )}
                </div>
            </Popup>
        </>
    );
});

// Dev Tools
ViewToolbarFieldChooser.displayName = 'ViewToolbarFieldChooser';

export default ViewToolbarFieldChooser;
